import { useState, useEffect } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  PackagePlus,
  PackageMinus,
  History,
  Settings,
  LogOut,
  Menu,
  Sun,
  Moon,
  HandCoins,
  WifiOff,
  AlertCircle,
  LogIn,
  X,
  ShieldCheck,
  Lock,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useAuth } from '@/shared/context/AuthContext'
import { useTheme } from '@/shared/context/ThemeContext'
import { useOffline } from '@/shared/context/OfflineContext'
import { useOperatorMode } from '@/shared/context/OperatorModeContext'
import { PIN_LENGTH, MIN_PIN } from '@/shared/lib/operatorMode'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog'
import { OfflineFailedSheet } from './OfflineFailedSheet'
import { PwaInstallBanner } from './PwaInstallBanner'

const CORE_NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/session/order', label: 'Order', icon: ShoppingCart },
  { to: '/session/restock', label: 'Restock', icon: PackagePlus },
  { to: '/session/adjustment', label: 'Adjustment', icon: PackageMinus },
  { to: '/history', label: 'History', icon: History },
  { to: '/credit', label: 'Credit', icon: HandCoins },
]

const OPERATOR_ROUTES = ['/products', '/session/order', '/history', '/credit']

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showFailedSheet, setShowFailedSheet] = useState(false)
  const [offlineBannerDismissed, setOfflineBannerDismissed] = useState(false)
  const [switchOpen, setSwitchOpen] = useState(false)
  const [switchPin, setSwitchPin] = useState('')
  const [switchError, setSwitchError] = useState('')
  const [switching, setSwitching] = useState(false)
  const { user, org, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { online, pendingCount, failedCount, failedItems, authError, retryItem, discardItem } = useOffline()
  const { isOperatorMode, disableOperatorMode } = useOperatorMode()

  const location = useLocation()
  const navigate = useNavigate()
  const isBranch = org?.master_org_id != null

  // Redirect operator away from restricted routes
  useEffect(() => {
    if (!isOperatorMode) return
    const allowed = OPERATOR_ROUTES.some(r => location.pathname.startsWith(r))
    if (!allowed) navigate('/products', { replace: true })
  }, [isOperatorMode, location.pathname])

  async function handleSwitchToOwner() {
    if (!switchPin.trim()) return
    setSwitching(true)
    setSwitchError('')
    const ok = await disableOperatorMode(switchPin)
    setSwitching(false)
    if (!ok) {
      setSwitchError('Incorrect PIN. Try again.')
      setSwitchPin('')
    } else {
      setSwitchOpen(false)
      setSwitchPin('')
    }
  }

  useEffect(() => {
    if (online) setOfflineBannerDismissed(false)
  }, [online])

  const visibleNavItems = isOperatorMode
    ? CORE_NAV_ITEMS.filter(item => OPERATOR_ROUTES.some(r => item.to.startsWith(r)))
    : CORE_NAV_ITEMS

  const ALL_NAV_LABELS = [
    ...CORE_NAV_ITEMS,
    { to: '/settings', label: 'Settings' },
  ]
  const pageLabel = ALL_NAV_LABELS.find(item => location.pathname.startsWith(item.to))?.label ?? ''

  // Auto-close the failed sheet when the last item is resolved
  useEffect(() => {
    if (failedCount === 0) setShowFailedSheet(false)
  }, [failedCount])

  async function handleSignOut() {
    await signOut()
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex w-56 flex-col bg-sidebar border-r border-sidebar-border transition-transform duration-200 lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Brand */}
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-sidebar-border px-4">
          <img src="/favicon.svg" alt="Shelvi" className="size-5 shrink-0 rounded-sm" />
          <span className="font-heading font-bold tracking-tight text-sidebar-foreground">Shelvi</span>
          {isOperatorMode && (
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
              Operator
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          {visibleNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                )
              }
            >
              <Icon className="size-4 shrink-0" />
              {label}
            </NavLink>
          ))}
          {!isOperatorMode && (
            <>
              <div className="mx-1 my-2 h-px bg-sidebar-border" />
              <NavLink
                to="/settings"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                  )
                }
              >
                <Settings className="size-4 shrink-0" />
                Settings
              </NavLink>
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="shrink-0 border-t border-sidebar-border p-3 space-y-1">
          {isOperatorMode ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-3 border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800/50 dark:text-amber-400 dark:hover:bg-amber-900/20"
              onClick={() => { setSwitchOpen(true); setSidebarOpen(false) }}
            >
              <ShieldCheck className="size-4" />
              Switch to owner
            </Button>
          ) : (
            <>
              {isBranch ? (
                <div className="px-3 py-1">
                  <p className="truncate text-xs font-medium text-sidebar-foreground">{org?.name}</p>
                </div>
              ) : (
                <p className="truncate px-3 py-1 text-xs text-muted-foreground">{user?.email}</p>
              )}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 justify-start gap-3 text-sidebar-foreground hover:text-destructive"
                  onClick={handleSignOut}
                >
                  <LogOut className="size-4" />
                  Sign out
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  className="shrink-0 text-sidebar-foreground hover:text-sidebar-accent-foreground"
                >
                  {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Main area */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile topbar */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <Menu className="size-5" />
          </Button>
          {pageLabel && (
            <span className="font-heading text-sm font-semibold tracking-tight text-foreground">{pageLabel}</span>
          )}
          {isOperatorMode && (
            <button
              onClick={() => setSwitchOpen(true)}
              className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-full"
            >
              <ShieldCheck className="size-3" />
              Operator
            </button>
          )}
        </header>

        {!online && !offlineBannerDismissed && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <WifiOff className="size-3.5 shrink-0" />
            <span className="flex-1">
              Offline Mode. Orders, Restocks and Adjustments sync when online.
              {pendingCount > 0 && <strong className="ml-1">{pendingCount} pending.</strong>}
            </span>
            <button
              onClick={() => setOfflineBannerDismissed(true)}
              className="shrink-0 rounded p-0.5 hover:bg-amber-200/60 dark:hover:bg-amber-800/60"
              aria-label="Dismiss offline banner"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}

        {authError && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <LogIn className="size-3.5 shrink-0" />
            <span>Please log in again to save your pending sales.</span>
          </div>
        )}

        {failedCount > 0 && (
          <div className="flex items-center gap-2 border-b border-destructive/20 bg-destructive/5 px-4 py-2 text-sm text-destructive dark:border-destructive/30 dark:bg-destructive/10">
            <AlertCircle className="size-3.5 shrink-0" />
            <span className="flex-1">
              {failedCount} {failedCount === 1 ? 'item' : 'items'} couldn&apos;t be saved.
            </span>
            <Button
              variant="ghost"
              size="xs"
              className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setShowFailedSheet(true)}
            >
              View
            </Button>
          </div>
        )}

        <PwaInstallBanner />

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {showFailedSheet && (
        <OfflineFailedSheet
          items={failedItems}
          currency={org?.currency ?? 'PHP'}
          onRetry={(id) => { void retryItem(id) }}
          onDiscard={(id) => { void discardItem(id) }}
          onClose={() => setShowFailedSheet(false)}
        />
      )}

      {/* Switch to owner PIN dialog */}
      <Dialog open={switchOpen} onOpenChange={open => { if (!open) { setSwitchOpen(false); setSwitchPin(''); setSwitchError('') } }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Lock className="size-4" />
              Switch to owner
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">Enter your owner PIN to exit operator mode.</p>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={PIN_LENGTH}
              value={switchPin}
              onChange={e => { setSwitchPin(e.target.value.replace(/\D/g, '')); setSwitchError('') }}
              onKeyDown={e => { if (e.key === 'Enter') void handleSwitchToOwner() }}
              placeholder="Enter PIN"
              autoFocus
            />
            {switchError && <p className="text-xs text-destructive">{switchError}</p>}
            <Button
              className="w-full"
              onClick={() => void handleSwitchToOwner()}
              disabled={switchPin.length < MIN_PIN || switching}
            >
              {switching ? 'Verifying…' : 'Unlock'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
